import { getAnalytics, logEvent, setUserProperties, isSupported } from 'firebase/analytics';
import type { Analytics } from 'firebase/analytics';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { firebase } from './firebase.js';
import { readEnv } from './env.js';
import { isNative } from './device.js';

/**
 * GA4 event layer (docs/ARCHITECTURE.md §2.8).
 *
 * Hard rule (CLAUDE.md #8): no health values ever reach GA4 — no angles, no
 * scores, no diagnosis codes. Only buckets, booleans and counts.
 *
 * That rule is enforced by the type system, not by review: `NoRawNumbers<T>`
 * turns any plain `number` parameter into `never`, so
 *   track('session_end', { esa: 43.2 })            // ✗ compile error
 *   track('session_end', { valid_ratio_bucket: '>80' })  // ✓
 * Counts are allowed only where the event map declares a literal-union or a
 * branded `CountParam`.
 */

/** A count is a number we explicitly allow (never a measurement). */
export type CountParam = number & { readonly __count: unique symbol };
export function count(n: number): CountParam {
  return Math.max(0, Math.round(n)) as CountParam;
}

/**
 * Maps every plain `number` member to `never`, so passing a raw measurement is a
 * type error. Literal unions (`1 | 2 | 3`) and `CountParam` survive.
 */
export type NoRawNumbers<T> = {
  [K in keyof T]: number extends T[K] ? never : T[K];
};

export type MinuteBucket = '15' | '30' | '60' | '120' | '180';
export type ValidRatioBucket = '<50' | '50-80' | '>80';
export type LatencyBucket = '<2s' | '2-5s' | '5-10s' | '>10s';
export type DaysBucket = '0-3' | '4-7' | '8-14' | '15+';

export interface AnalyticsEventMap {
  onboarding_step: { step: 1 | 2 | 3 | 4 | 5 | 6 | 7; completed: boolean };
  capture_setup_check: {
    check: 'side_view' | 'ear' | 'hip' | 'light' | 'multi_person' | 'phone_upright';
    passed: boolean;
  };
  calibration_done: { duration_bucket: '<5s' | '5-10s' | '>10s' };
  session_start: {
    planned_min_bucket: MinuteBucket;
    device_class: 'low' | 'mid' | 'high';
    pose_model: 'lite' | 'full';
    fps: 2 | 3 | 4 | 5;
  };
  session_pause: { reason: 'user' | 'background' | 'multi_person' | 'gate_failed' | 'break' };
  session_resume: { reason: 'user' | 'foreground' | 'gate_recovered' };
  session_end: {
    actual_min_bucket: MinuteBucket;
    valid_ratio_bucket: ValidRatioBucket;
    ended_by: 'user' | 'error' | 'background';
  };
  /** A session thrown away at Finish — nothing was saved. */
  session_discarded: { elapsed_min_bucket: MinuteBucket };
  session_deleted: { from: 'report' };
  /** The phone could not keep the fixed sample rate for a minute (once per session). */
  capture_slow: { fps_bucket: '<1' | '1-2' | '2-3' | '3+'; pose_model: 'lite' | 'full' };
  posture_check_done: { kind: 'seated' | 'standing' };
  /** The app healed itself: the camera reopened, or the pose model was rebuilt. */
  capture_recovered: { kind: 'stream' | 'tracking'; ok: boolean };
  session_error: {
    code: 'camera_denied' | 'model_load' | 'gpu_fail' | 'lost_tracking' | 'storage_full';
  };
  report_view: { has_coaching: boolean };
  /** The skeleton replay opened from the report — its button, or a ten-minute card — and where its frames came from. */
  replay_open: { from: 'button' | 'snapshot'; speed: '10x' | '60x' | '240x'; source: 'device' | 'account' };
  /** Sharing turned on: how many sessions still on the phone were sent with it. */
  keypoint_backfill: { sessions: CountParam };
  /** The dismissible pointer to the data opt-in shown beside replays. */
  data_optin_prompt: { action: 'open' | 'dismiss' };
  feedback_submit: { overall: 'accurate' | 'inaccurate'; ranges_count: CountParam };
  coaching_generated: {
    provider: 'gemini' | 'claude';
    latency_bucket: LatencyBucket;
    safety_flag_count: CountParam;
  };
  coaching_rated: { rating: 1 | 2 | 3 | 4 | 5 };
  exercise_view: { exercise_id: string };
  exercise_complete: { exercise_id: string };
  reminder_set: { every_days: 3 | 7 };
  reminder_open: { days_since_last_session_bucket: DaysBucket };
  data_optin: { value: boolean };
  badge_earned: { badge_id: string };
  stage_change: { from: 1 | 2 | 3 | 4 | 5; to: 1 | 2 | 3 | 4 | 5 };
  landing_cta_click: { cta_id: string };
  /** Program picked (onboarding or Program screen) or stopped. */
  program_start: { program_id: string; from: 'onboarding' | 'program' };
  program_stop: { program_id: string; week: 1 | 2 | 3 | 4 | 5 | 6 };
  /** A rest break logged; where it came from. */
  break_logged: { source: 'nudge' | 'manual' };
  snack_done: { snack_id: string; in_program: boolean };
  account_deleted: { confirmed: boolean };
}

export type AnalyticsEventName = keyof AnalyticsEventMap;

export interface AnalyticsUserProperties {
  stage: '1' | '2' | '3' | '4' | '5';
  reminder_days: '3' | '7';
  has_data_optin: 'true' | 'false';
  platform: 'web' | 'ios' | 'android';
}

let analytics: Analytics | null = null;
let ready: Promise<void> | null = null;
/**
 * Usage statistics are on unless the person turned them off in Settings
 * (Your data). They carry no health values (see NoRawNumbers above). The
 * native apps send through the Firebase SDK — the web SDK cannot set cookies
 * on capacitor:// and never reported — and the web through gtag.
 */
let consentGranted = false;
const CONSENT_KEY = 'erectus.analyticsConsent';

export function setAnalyticsConsent(granted: boolean): void {
  consentGranted = granted;
  if (isNative()) void FirebaseAnalytics.setEnabled({ enabled: granted }).catch(() => undefined);
}

/**
 * Stored choice: anything but an explicit 'denied' counts as on — except that
 * a browser sending Global Privacy Control starts off, as the policy promises.
 */
export function storedAnalyticsConsent(): boolean {
  try {
    const stored = window.localStorage.getItem(CONSENT_KEY);
    if (stored) return stored !== 'denied';
  } catch {
    // Fall through to the default.
  }
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl !== true;
}

export function saveAnalyticsConsent(granted: boolean): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied');
  } catch {
    // The choice still applies for this run.
  }
  setAnalyticsConsent(granted);
}

/** Screen views: one per route, ids stripped (/report/abc → /report). */
export function trackScreen(pathname: string): void {
  if (!consentGranted) return;
  const screen = pathname.split('/').filter(Boolean)[0] ?? 'home';
  if (isNative()) {
    void FirebaseAnalytics.setCurrentScreen({ screenName: screen, screenClassOverride: screen }).catch(() => undefined);
    return;
  }
  void ensure().then(() => {
    if (analytics) logEvent(analytics, 'screen_view' as string, { firebase_screen: screen, firebase_screen_class: screen });
  });
}

async function ensure(): Promise<void> {
  if (ready) return ready;
  ready = (async () => {
    if (!readEnv().measurementId) return;
    if (!(await isSupported())) return;
    analytics = getAnalytics(firebase().app);
  })();
  return ready;
}

/** Buffer events fired before the SDK finished loading, so onboarding funnels are complete. */
const queue: Array<[string, Record<string, unknown>]> = [];

export function track<E extends AnalyticsEventName>(
  event: E,
  params: NoRawNumbers<AnalyticsEventMap[E]>,
): void {
  if (!consentGranted) return;
  const payload = params as Record<string, unknown>;
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[ga4]', event, payload);
  }
  if (isNative()) {
    void FirebaseAnalytics.logEvent({ name: event as string, params: payload }).catch(() => undefined);
    return;
  }
  if (!analytics) {
    queue.push([event, payload]);
    void ensure().then(() => flush());
    return;
  }
  logEvent(analytics, event as string, payload);
}

function flush(): void {
  if (!analytics) return;
  while (queue.length > 0) {
    const item = queue.shift();
    if (!item) break;
    logEvent(analytics, item[0], item[1]);
  }
}

export function setProps(props: Partial<AnalyticsUserProperties>): void {
  if (!consentGranted) return;
  if (isNative()) {
    for (const [key, value] of Object.entries(props)) {
      void FirebaseAnalytics.setUserProperty({ key, value: String(value) }).catch(() => undefined);
    }
    return;
  }
  void ensure().then(() => {
    if (analytics) setUserProperties(analytics, props);
  });
}

// ── bucket helpers: the only sanctioned way a measurement becomes an event ────

export function minuteBucket(minutes: number): MinuteBucket {
  if (minutes <= 15) return '15';
  if (minutes <= 30) return '30';
  if (minutes <= 60) return '60';
  if (minutes <= 120) return '120';
  return '180';
}

export function validRatioBucket(ratio: number): ValidRatioBucket {
  if (ratio < 0.5) return '<50';
  if (ratio <= 0.8) return '50-80';
  return '>80';
}

export function latencyBucket(ms: number): LatencyBucket {
  if (ms < 2000) return '<2s';
  if (ms < 5000) return '2-5s';
  if (ms < 10000) return '5-10s';
  return '>10s';
}

export function daysBucket(days: number): DaysBucket {
  if (days <= 3) return '0-3';
  if (days <= 7) return '4-7';
  if (days <= 14) return '8-14';
  return '15+';
}

export function durationBucket(ms: number): '<5s' | '5-10s' | '>10s' {
  if (ms < 5000) return '<5s';
  if (ms <= 10000) return '5-10s';
  return '>10s';
}
